import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import PrimaryButton from "./components/_utils/CommonButtons/PrimaryButton";
import { getRefreshTokenNew } from "./redux/features/refreshToken/refreshToken";
import ICONS from "./assets/Icons";
import { webSocketTokenThunk } from "./redux/features/login/login";
import {
  addNotificationToStore,
  notificationsBulkEdit,
} from "./redux/features/Notifications";
import useDynamicRouting from "./utils/useDynamicRouting";
// Import the functions you need from the SDKs you need
import { generateToken, messaging } from "./firebase";
import { onMessage } from "firebase/messaging";
import { isNotificationTypeAllowed } from "./utils/notificationFilter";
import { env } from "./env";
import { syncNotificationConfigFromStoredPrefs } from "./containers/Settings/NotificationCenter";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error(error);
    console.error(errorInfo);
  }

  handleClick = () => {
    window.location.href = "https://www.q2pay.in/landing/contact";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full min-h-screen flex justify-center items-center p-padding-md">
          <div className="w-full max-w-tab-width-normal bg-primary-nav rounded-border-radius-md p-padding-xl flex flex-col lg:flex-row items-center justify-center gap-gap-xl">
            <div className="flex justify-center items-center mb-margin-md lg:mb-margin-base">
              <img
                className=" h-graphic-rectangle-height-lg w-graphic-width-lg"
                src={ICONS?.UNKNOWN_ERROR}
                alt="oops"
              />
            </div>

            <div className="w-full lg:w-width-md flex flex-col justify-center items-center text-center">
              <div className="text-5xl text-text-primary mb-margin-md font-sans">
                Oops!
              </div>

              <div className="text-text-primary pb-padding-xl text-base">
                An unknown error has occurred,
                <br /> kindly retry the operation later.
              </div>

              <div className="text-text-secondary text-sm">
                If the problem still persists
                <br />
                contact &nbsp;
                <span
                  className="text-pending underline cursor-pointer"
                  onClick={this.handleClick}
                >
                  customer support
                </span>
              </div>

              <div className="flex flex-row justify-center items-center gap-gap-md w-full mt-margin-xl">
                <PrimaryButton
                  enabledBgColor=""
                  btnText="Try Again"
                  outLineColor="var(--text-primary)"
                  enabledTextColor="var(--text-primary)"
                  handleFunction={() => window.location.reload()}
                  hoverOption="white"
                />

                <HomeButton />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export const HomeButton = () => {
  const handleHomeClick = () => {
    window.location.reload();
    window.location.href = "/home";
  };

  return (
    <>
      <PrimaryButton
        enabledBgColor="var(--text-primary)"
        btnText="Home"
        enabledTextColor="var(--primary-bg)"
        handleFunction={handleHomeClick}
        hoverOption="black"
      />
    </>
  );
};

/**
 * Builds a dynamic route path from a notification's `eventable_type` and `eventable_id`.
 *
 * This function parses the type to extract:
 * - `flow`: the feature area (e.g., "beneficiary", "reimbursement") — everything before the last underscore
 * - `flowType`: the specific action (e.g., "transction", "account") — the last segment after underscore
 *
 * Then constructs a route following the app's nested routing convention:
 * - Single entity: `/{flow}_{flowType}_dashboard/{flow}_{flowType}_view_all/{flow}_{flowType}_detail_page/{id}`
 * - Bulk (array of IDs): `/{flow}_{flowType}_dashboard/{flow}_{flowType}_view_all`
 *
 * The `id` parameter can be a stringified JSON array (for bulk notifications).
 * If so, it's parsed and the route points to the list view instead of a detail page.
 *
 * @param {string} type - The `eventable_type` from the notification payload
 *   (e.g., "beneficiary_transaction", "internal_account").
 * @param {string|number} id - The `eventable_id` (single) or `eventable_ids` (array as JSON string).
 * @returns {string} A relative URL path for react-router navigation.
 *
 * @example
 * getDynamicRoute("beneficiary_transaction", "123")
 * // → "/beneficiary_transaction_dashboard/beneficiary_transaction_view_all/beneficiary_transaction_detail_page/123"
 *
 * @example
 * getDynamicRoute("internal_account", "[1,2,3]")
 * // → "/internal_account_dashboard/internal_account_view_all"
 */
const getDynamicRoute = (type, id) => {
  // Extract the "flow" (feature area) by taking everything before the last underscore.
  // The nameConversion utility normalizes the string (lowercase, underscores).
  let flow = type
    ?.split("_")
    ?.slice(0, -1)
    ?.join(",")
    ?.replaceAll(",", "_")
    ?.toLowerCase();
  let parsedId = id;

  // Handle bulk notifications where `eventable_ids` arrives as a JSON string array.
  // e.g., '[1,2,3]' → [1,2,3] → routes to list view instead of detail page.
  if (typeof id === "string") {
    try {
      const parsed = JSON.parse(id);

      if (Array.isArray(parsed)) {
        parsedId = parsed;
      }
    } catch {
      // Not valid JSON — treat as single ID string/number.
    }
  }

  // The last segment after the final underscore is the "flowType" (action).
  let flowType = type?.split("_")?.slice(-1);

  // Bulk (array) → list view; Single → detail page with ID.
  if (type === "beneficiary_schedule_transaction") {
    return `/${flow}_transaction_dashboard/schedule_view_all/schedule_detail_page/${parsedId}`;
  } else if (type === "bulk_transaction") {
    return `/beneficiary_transaction_dashboard/bulk_transaction_view_all/bulk_transaction_detail_page/${parsedId}`;
  } else {
    if (Array.isArray(parsedId)) {
      return `/${flow}_${flowType}_dashboard/${flow}_${flowType}_view_all`;
    } else {
      return `/${flow}_${flowType}_dashboard/${flow}_${flowType}_view_all/${flow}_${flowType}_detail_page/${parsedId}`;
    }
  }
};

function App() {
  const { conciergeLoading } = useSelector((state) => state.multientity);
  const [showModalPopup, setShowModalPopup] = useState(false);
  const { data } = useSelector((state) => state.refreshToken);
  const { services } = useSelector((state) => state.refreshToken);
  const { showReloginModal, q2p_token } = useSelector((state) => state.login);
  const { markerData } = useSelector((s) => s.multiEntitySwitcherSlice);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const { webSocketToken } = useSelector((state) => state.login);
  const socketRef = useRef(null);
  const swRegistrationRef = useRef(null);
  const handleDynamicRoutingRef = useRef(null);
  const hasFlushedPendingReadNotificationsRef = useRef(false);

  useEffect(() => {
    if (new URLSearchParams(search).get("notification_click") === "true") {
      localStorage.setItem("notification_click", "notification_click");
      sessionStorage.setItem("notification_click_pending", "true");
    }
  }, [search]);

  useEffect(() => {
    const isNotificationNavigation =
      state?.notificationRoute === pathname ||
      state?.fromNotification === true ||
      new URLSearchParams(search).get("notification_click") === "true";

    if (isNotificationNavigation) {
      sessionStorage.setItem("notification_click_route", pathname);
      sessionStorage.setItem("notification_click_pending", "true");

      localStorage.removeItem("notification_click");
    }
  }, [pathname, search, state]);

  useEffect(() => {
    const notificationRoute = sessionStorage.getItem(
      "notification_click_route",
    );

    const notificationPending =
      sessionStorage.getItem("notification_click_pending") === "true";

    if (!notificationRoute || !notificationPending) {
      return;
    }

    if (pathname === "/relogin" || pathname === "/login" || pathname === "/") {
      return;
    }

    const isNotificationRoute =
      pathname === notificationRoute ||
      pathname.startsWith(`${notificationRoute}/`);

    if (isNotificationRoute) {
      sessionStorage.removeItem("notification_click_pending");
    }
  }, [pathname]);

  useEffect(() => {
    if (
      hasFlushedPendingReadNotificationsRef.current ||
      (!services?.includes("Notify") && !services?.includes("Admin"))
    ) {
      return;
    }

    const storedIds = sessionStorage.getItem("pending_notification_read_ids");
    if (!storedIds) return;

    let notificationIds;

    try {
      notificationIds = JSON.parse(storedIds);
    } catch (error) {
      console.error("Failed to parse pending notification read IDs", error);
      sessionStorage.removeItem("pending_notification_read_ids");
      return;
    }

    if (!Array.isArray(notificationIds) || !notificationIds.length) {
      sessionStorage.removeItem("pending_notification_read_ids");
      return;
    }

    hasFlushedPendingReadNotificationsRef.current = true;

    dispatch(
      notificationsBulkEdit({
        read: true,
        notification_ids: notificationIds,
      }),
    ).then((response) => {
      if (response?.meta?.requestStatus === "fulfilled") {
        sessionStorage.removeItem("pending_notification_read_ids");
      } else {
        hasFlushedPendingReadNotificationsRef.current = false;
      }
    });
  }, [dispatch, services]);

  useEffect(() => {
    /**
     * Registers the Firebase Cloud Messaging (FCM) service worker and
     * retrieves a push token, but only when the user is logged in and has
     * already granted notification permission. Designed to be safely
     * re-invoked (e.g. on permission changes) since it re-registers the
     * service worker each time rather than assuming a prior registration.
     */

    const setupFCM = async () => {
      // Check login status from sessionStorage (set during authentication flow)
      const isLoggedIn =
        JSON.parse(sessionStorage.getItem("loggedIn")) === true;

      // Proceed only if service workers are supported and user has an active session
      if ("serviceWorker" in navigator && isLoggedIn) {
        try {
          // 1. Request notification permission if supported
          if ("Notification" in window && sessionStorage.getItem("q2p_token")) {
            if (Notification.permission === "granted") {
              console.log("Notification permission granted");

              // 2. Register the service worker explicitly at root scope
              const registration = await navigator.serviceWorker.register(
                "/firebase-messaging-sw.js",
              );

              // Store the active registration for later use (e.g., showing notifications from foreground messages)
              // If registration isn't active yet, wait for `navigator.serviceWorker.ready`
              swRegistrationRef.current = registration.active
                ? registration
                : await navigator.serviceWorker.ready;
              console.log("Service Worker registered:", registration);

              // 3. Generate an FCM device token bound to this service worker registration
              const fcmToken = await generateToken(registration);

              // Send token + current user notification preferences to the service worker
              // so it can filter/route background pushes appropriately
              await syncNotificationConfigFromStoredPrefs(fcmToken);
            }
          }
        } catch (error) {
          console.error("FCM setup failed:", error);
        }
      } else {
        console.log("Service worker not supported or user not logged in.");
      }
    };

    setupFCM();

    // Listen for runtime changes to notification permission (e.g., user clicks "Allow" in browser prompt)
    // The Permissions API lets us detect when permission state changes without polling
    if (navigator.permissions) {
      navigator.permissions.query({ name: "notifications" }).then((status) => {
        status.onchange = async () => {
          // If permission becomes granted, re-run the full FCM setup to register SW and generate token
          if (Notification?.permission === "granted") {
            setupFCM();
          }
        };
      });
    }
  }, [q2p_token]);
  const { handleDynamicRouting } = useDynamicRouting();

  useEffect(() => {
    handleDynamicRoutingRef.current = handleDynamicRouting;
  }, [handleDynamicRouting]);

  useEffect(() => {
    const checkSession = async () => {
      const hasSessionExpired = window.sessionStorage.getItem("q2p_token");

      if (hasSessionExpired) {
        try {
          dispatch(getRefreshTokenNew());
          dispatch(webSocketTokenThunk());
        } catch (error) {
          setShowModalPopup(true);
        }
      }
    };
    checkSession();
  }, [dispatch, pathname, markerData]);

  useEffect(() => {
    if (data?.employees?.first_login_flag == "Y") {
      navigate("/setNewPassword");
    }
  }, [data]);
  /**
   * Establishes a WebSocket connection for real-time notifications via ActionCable.
   *
   * Runs when `webSocketToken` changes (on login/token refresh). Connects to the
   * WebSocket endpoint with the token as a query param, subscribes to
   * `NotificationsChannel`, and forwards incoming notification messages to Redux.
   *
   * iOS Safari blocks Web Push API unless the site is installed as a PWA
   * (added to Home Screen). This effect detects that case and logs a warning
   * instead of attempting to show notifications.
   *
   * @listens `webSocketToken` from Redux (login slice)
   * @sideeffect
   *   - Creates WebSocket connection stored in `socketRef.current`
   *   - Dispatches `addNotificationToStore` for each notification message
   * @cleanup Closes WebSocket on unmount or when `webSocketToken` changes.
   */
  useEffect(() => {
    /**
     * Detects if the app is running as an installed PWA (standalone display mode).
     * iOS requires PWA installation for Web Push / Service Worker notifications.
     * @returns {boolean}
     */
    function isRunningAsPWA() {
      return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
      );
    }

    // Guard: no token = no authenticated WebSocket connection possible
    if (!webSocketToken?.ws_q2p_token) return;
    const socket = new WebSocket(
      `${env.REACT_APP_WEBSOCKET_BASE_URL}/cable?token=${webSocketToken.ws_q2p_token}`,
    );

    socketRef.current = socket;

    socket.onopen = () => {
      console.log("WebSocket Connected");

      // ActionCable subscription protocol: send JSON with command + channel identifier
      socket.send(
        JSON.stringify({
          command: "subscribe",
          identifier: JSON.stringify({
            channel: "NotificationsChannel",
          }),
        }),
      );
    };

    socket.onmessage = async (event) => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      // iOS Safari only delivers push notifications if the site is installed as a PWA.
      // If not installed, warn and skip processing (the server may still deliver via other channels).
      if (isIOS && !isRunningAsPWA()) {
        console.warn(
          "iOS requires this site to be added to the Home Screen first.",
        );
        return;
      }

      const response = JSON.parse(event.data);

      // Ignore ActionCable internal messages (heartbeat, welcome, subscription confirmation)
      if (
        response.type === "ping" ||
        response.type === "welcome" ||
        response.type === "confirm_subscription"
      ) {
        return;
      }

      // Actual notification payload arrives under `response.message`
      if (response.message) {
        dispatch(addNotificationToStore(response.message));
      }
    };

    socket.onerror = (error) => {
      console.error("WS Error", error);
    };

    socket.onclose = () => {
      console.log("WS Closed");
    };

    return () => {
      socket.close();
    };
  }, [webSocketToken, dispatch]);

  /**
   * Handles messages posted by the service worker:
   *  - FCM_FOREGROUND: a push arrived while a tab is visible. The SW does NOT
   *    show an OS notification in this case, so the page decides (preferences
   *    check + showNotification).
   *
   *  - NOTIFICATION_CLICK: user clicked a notification and a tab already exists.
   */
  useEffect(() => {
    function isRunningAsPWA() {
      return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
      );
    }

    const handleForegroundMessage = async (payload) => {
      console.log("FCM foreground message received:", payload);

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS && !isRunningAsPWA()) {
        console.warn(
          "iOS requires this site to be added to the Home Screen first.",
        );
        return;
      }

      if (!payload) return;

      const data = payload.data || {};
      const title =
        payload.notification?.title || data.title || "New Notification";
      const body =
        payload.notification?.body ||
        data.body ||
        data.description ||
        data.message ||
        "You have received a new notification";

      const eventableType = data.eventable_type;
      const eventableId = data.eventable_id || data.eventable_ids;

      // Per-type preference check (e.g. "transaction","account",etc...)
      const notificationTypeIsAllowed = await isNotificationTypeAllowed(
        eventableType?.split("_")?.slice(-1)?.[0],
      );

      if (
        !("Notification" in window) ||
        Notification.permission !== "granted" ||
        !notificationTypeIsAllowed
      ) {
        return;
      }

      const options = {
        body,
        icon: ICONS.XPENTRA_SYMBOL,
        badge: ICONS.XPENTRA_SYMBOL,
        data: { ...data, title },
        ...(eventableId && { tag: String(eventableId), renotify: true }),
      };

      if (swRegistrationRef.current) {
        swRegistrationRef.current.showNotification(title, options);
      } else {
        const notification = new Notification(title, options);

        notification.onclick = () => {
          window.focus();
          localStorage.setItem("notification_click", "notification_click");
          handleDynamicRoutingRef.current?.(eventableType, eventableId);
          notification.close();
        };
      }
    };

    const handler = (event) => {
      const msg = event.data;
      if (!msg) return;

      if (msg.type === "FCM_FOREGROUND") {
        handleForegroundMessage(msg.payload);
        return;
      }

      if (msg.type === "NOTIFICATION_CLICK") {
        const { eventable_type, eventable_id, eventable_ids, title } =
          msg.payload;

        localStorage.setItem("notification_click", "notification_click");
        const targetUrl = new URL(
          getDynamicRoute(eventable_type, eventable_id || eventable_ids),
          window.location.origin,
        );
        targetUrl.searchParams.set("notification_click", "true");

        // Checks if the resolved route ends with 'view_all' (matches bulk/list routes)
        const isViewAllRoute = targetUrl.pathname.endsWith("view_all");

        const txn_id = (() => {
          try {
            return eventable_ids ? JSON.parse(eventable_ids) : eventable_id;
          } catch {
            return {};
          }
        })();

        navigate(`${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`, {
          state: {
            fromNotification: true,
            notificationRoute: targetUrl.pathname,
            approvalStatus: title?.toLowerCase()?.includes("approved")
              ? "approved"
              : "rejected",
            // Only attach txn_id if route ends with "view_all"
            ...(isViewAllRoute && { txn_id }),
          },
        });
      }
    };

    navigator.serviceWorker.addEventListener("message", handler);

    return () => {
      navigator.serviceWorker.removeEventListener("message", handler);
    };
  }, [dispatch]);

  /**
   * Listens for `NOTIFICATION_RECEIVED` messages from the service worker.
   *
   * When the SW receives a background push and shows
   * a notification, it posts a `NOTIFICATION_RECEIVED` message to all clients.
   * This handler captures the `entity_id` from the payload and stores it in
   * `localStorage` for potential use by other parts of the app.
   *
   * Runs once on mount (empty dependency array).
   *
   */
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type === "NOTIFICATION_RECEIVED") {
        const entity_id = event.data?.payload?.entity_id;

        if (entity_id) {
          localStorage.setItem(
            "notification_entity_id_background_msg",
            entity_id,
          );
        }
      }
    };

    navigator.serviceWorker.addEventListener(
      "message",
      handleServiceWorkerMessage,
    );

    return () => {
      navigator.serviceWorker.removeEventListener(
        "message",
        handleServiceWorkerMessage,
      );
    };
  }, []);

  /**
   * Public routes that don't require authentication.
   * Used to allow unauthenticated access to marketing/login pages.
   */
  const safePaths = new Set([
    "/login",
    "/",
    "/contactAdmin",
    "/product",
    "/pricing",
    "/aboutus",
    "/advisors",
    "/contactus",
    "/book_demo",
  ]);

  useEffect(() => {
    const tokenMissing = !sessionStorage.getItem("q2p_token");
    const isSafePath = safePaths.has(pathname);

    if (showReloginModal && !conciergeLoading && !isSafePath) {
      if (tokenMissing) {
        sessionStorage.removeItem("loggedIn");
      }

      // ✅ Store the first pathname only once
      if (!sessionStorage.getItem("previousLog")) {
        sessionStorage.setItem("previousLog", pathname);
      }

      navigate("/relogin", { replace: true });
    }
  }, [showReloginModal, conciergeLoading, navigate, pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="App">
      <ErrorBoundary>
        <Outlet />
        <ToastContainer
          style={{ zIndex: "var(--max-zindex)" }}
          className="toast-container"
        />
      </ErrorBoundary>
    </div>
  );
}

export default App;
